import { type ReactNode } from "react";
import {
  CalendarPlus,
  FileText,
  MessageSquare,
  NotebookPen,
  Sparkles,
  Upload,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
  CoachLeaderDetailPresentation,
  CoachLeaderDetailPrimaryAction,
  CoachLeaderDetailRecentContextFact,
  CoachLeaderDetailUpcomingSessionFact,
} from "@/lib/coachLeaderDetail/buildCoachLeaderDetailPresentation";

export interface CoachLeaderDetailWorkspaceProps {
  readonly leaderName: string;
  readonly presentation: CoachLeaderDetailPresentation;
  readonly nextMeeting: CoachLeaderDetailUpcomingSessionFact | null;
  readonly nextMeetingStatus: "loading" | "ready" | "retryable_error";
  readonly unreadMessageCount?: number | null;
  readonly privateNoteOpen?: boolean;
  readonly privateNoteEditor?: ReactNode;
  readonly billingResolutionPanel?: ReactNode;
  readonly operationalNoticePanel?: ReactNode;
  readonly unavailableDetail?: string | null;
  readonly onRetryContext?: () => void;
  readonly hasReviewContent?: boolean;
  readonly onOpenReviewContent?: () => void;
  readonly privateNoteStatus?: "ready" | "unavailable";
  readonly onPrimaryAction: (action: CoachLeaderDetailPrimaryAction) => void;
  readonly onScheduleMeeting: () => void;
  readonly onAskCoachVeera?: (invoker: HTMLButtonElement) => void;
  readonly onMessage: () => void;
  readonly onAddPrivateNote: () => void;
  readonly onUploadTranscript: () => void;
  readonly onOpenRecentContext: (fact: CoachLeaderDetailRecentContextFact) => void;
}

function formatWorkspaceDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatWorkspaceDateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function CoachLeaderDetailWorkspace({
  leaderName,
  presentation,
  nextMeeting,
  nextMeetingStatus,
  unreadMessageCount,
  privateNoteOpen = false,
  privateNoteEditor,
  billingResolutionPanel,
  operationalNoticePanel,
  unavailableDetail,
  onRetryContext,
  hasReviewContent = false,
  onOpenReviewContent,
  privateNoteStatus = "ready",
  onPrimaryAction,
  onScheduleMeeting,
  onAskCoachVeera,
  onMessage,
  onAddPrivateNote,
  onUploadTranscript,
  onOpenRecentContext,
}: CoachLeaderDetailWorkspaceProps) {
  if (presentation.status === "loading") {
    return (
      <section
        aria-label={`${leaderName} workspace`}
        aria-busy="true"
        className="rounded-[1.65rem] border border-[#d8c7ae]/70 bg-white px-6 py-12 text-center shadow-[0_24px_70px_-50px_rgba(76,54,28,0.46)] dark:border-[#2b2435] dark:bg-[#15101d]"
      >
        <p className="text-sm text-muted-foreground">Loading this leader workspace...</p>
      </section>
    );
  }

  const messageLabel = unreadMessageCount && unreadMessageCount > 0
    ? `Message, ${unreadMessageCount} unread`
    : "Message";

  return (
    <section
      aria-label={`${leaderName} next-conversation workspace`}
      className="@container overflow-hidden rounded-[1.65rem] border border-[#d8c7ae]/70 bg-white shadow-[0_24px_70px_-50px_rgba(76,54,28,0.46)] dark:border-[#2b2435] dark:bg-[#15101d]"
    >
      <div className="grid gap-8 px-5 py-6 sm:px-7 sm:py-8 @5xl:grid-cols-[minmax(0,1.35fr)_minmax(18rem,0.65fr)] @5xl:gap-10">
        <div className="min-w-0 space-y-8">
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7f5b43] dark:text-[#c9ad87]">
                Next conversation
              </p>
              <h2 className="font-serif text-3xl font-semibold tracking-tight text-[#251837] dark:text-[#f7f0e8]">
                What matters next
              </h2>
            </div>

            {presentation.priority ? (
              <div className="space-y-2 border-l-2 border-[#c98c56]/55 pl-4">
                <h3 className="text-xl font-semibold text-foreground">{presentation.priority.title}</h3>
                <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                  {presentation.priority.reason}
                </p>
              </div>
            ) : (
              <p className="text-sm leading-6 text-muted-foreground">
                No immediate action is needed. Keep the current context close for the next conversation.
              </p>
            )}

            {presentation.primaryAction ? (
              <Button
                type="button"
                className="min-h-11 w-full sm:w-auto"
                onClick={() => onPrimaryAction(presentation.primaryAction!)}
              >
                {presentation.primaryAction.label}
              </Button>
            ) : null}

            {billingResolutionPanel}
            {operationalNoticePanel}

            {unavailableDetail ? (
              <div className="rounded-xl border border-amber-300/40 bg-amber-500/5 px-4 py-3">
                <p className="text-sm font-medium text-foreground">Some coaching context is temporarily unavailable.</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{unavailableDetail}</p>
                {onRetryContext ? (
                  <Button type="button" variant="outline" className="mt-3" onClick={onRetryContext}>
                    Retry coaching context
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Context</p>
                <h2 className="text-xl font-semibold text-foreground">Recent coaching context</h2>
              </div>
              {presentation.status === "partial" ? (
                <span className="text-xs font-medium text-amber-700 dark:text-amber-200">Available context only</span>
              ) : null}
            </div>

            {presentation.unavailableSections.length > 0 ? (
              <div className="rounded-xl border border-amber-300/40 bg-amber-500/5 px-4 py-3">
                <p className="text-sm font-medium text-foreground">Some context is unavailable</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {presentation.unavailableSections.map((section) => (
                    <span key={section} className="rounded-full border border-amber-300/40 px-2.5 py-1 text-xs text-muted-foreground">
                      {section}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {presentation.recentContext.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {presentation.recentContext.slice(0, 2).map((item) => (
                  <article
                    key={`${item.sourceType}:${item.canonicalSourceId}`}
                    aria-label={`${item.title} coaching context`}
                    className="rounded-2xl border border-border/60 bg-[#fbf8f3]/70 p-4 dark:bg-white/[0.025]"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#7f5b43] dark:text-[#c9ad87]">
                        {item.sourceType.replace("_", " ")}
                      </span>
                      <time dateTime={item.occurredAt} className="text-xs text-muted-foreground">
                        {item.occurredAtLabel ?? "Observed"} {formatWorkspaceDate(item.occurredAt)}
                      </time>
                    </div>
                    <h3 className="mt-3 text-base font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-1 line-clamp-3 text-sm leading-6 text-muted-foreground">{item.summary}</p>
                    <Button
                      type="button"
                      variant="link"
                      className="mt-2 h-auto min-h-11 px-0"
                      onClick={() => onOpenRecentContext(item)}
                    >
                      View source
                    </Button>
                  </article>
                ))}
              </div>
            ) : (
              <p className="rounded-2xl border border-dashed border-border/70 px-4 py-5 text-sm leading-6 text-muted-foreground">
                No recent coaching context is ready yet.
              </p>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-5 @5xl:border-l @5xl:border-border/60 @5xl:pl-8">
          <section
            aria-label="Next meeting"
            className="relative overflow-hidden rounded-[1.45rem] border border-[#d8c7ae]/70 bg-[linear-gradient(145deg,#fffdf8_0%,#f6ede1_100%)] p-5 shadow-[0_16px_42px_-36px_rgba(76,54,28,0.65)] dark:border-[#d9c8ae]/20 dark:bg-[linear-gradient(145deg,rgba(53,35,73,0.76)_0%,rgba(30,20,42,0.94)_100%)]"
          >
            <div aria-hidden="true" className="absolute -right-10 -top-10 size-36 rounded-full bg-[#c98c56]/10 blur-2xl dark:bg-[#a576d8]/10" />
            <div className="relative space-y-5">
              <div className="flex items-start gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-[#c98c56]/35 bg-white/80 text-[#7f5b43] shadow-sm dark:border-[#d9c8ae]/20 dark:bg-white/[0.07] dark:text-[#e4c99f]">
                  <CalendarPlus className="size-4" />
                </span>
                <div className="min-w-0 space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7f5b43] dark:text-[#c9ad87]">Next conversation</p>
                  <h2 className="font-serif text-2xl font-semibold tracking-tight text-[#2f1b42] dark:text-[#f7f0e8]">Next meeting</h2>
                </div>
              </div>

              <div className="border-l-2 border-[#c98c56]/55 pl-4">
                {nextMeetingStatus === "loading" ? (
                  <p className="text-sm leading-6 text-muted-foreground">Checking upcoming meetings...</p>
                ) : nextMeetingStatus === "retryable_error" ? (
                  <p className="text-sm leading-6 text-muted-foreground">Upcoming meetings are unavailable.</p>
                ) : nextMeeting ? (
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">{nextMeeting.title}</p>
                    <time dateTime={nextMeeting.startsAt} className="block text-sm text-muted-foreground">
                      {formatWorkspaceDateTime(nextMeeting.startsAt)}
                    </time>
                  </div>
                ) : (
                  <p className="text-sm leading-6 text-muted-foreground">No upcoming meeting is scheduled.</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" className="min-h-11 bg-[#3d2159] text-white hover:bg-[#2f1945] dark:bg-[#c29af0] dark:text-[#20112e] dark:hover:bg-[#d2b5f5]" onClick={onScheduleMeeting}>
                  <CalendarPlus className="mr-2 h-4 w-4" />
                  Schedule meeting
                </Button>
                {onAskCoachVeera ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11 text-[#5d3a78] hover:bg-white/70 hover:text-[#3d2159] dark:text-[#decaf3] dark:hover:bg-white/[0.08] dark:hover:text-white"
                    onClick={(event) => onAskCoachVeera(event.currentTarget)}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Ask Coach Veera
                  </Button>
                ) : null}
              </div>
              {hasReviewContent && onOpenReviewContent ? (
                <Button type="button" variant="link" className="h-auto min-h-0 px-0 text-[#6f4f88] dark:text-[#d6b9f0]" onClick={onOpenReviewContent}>
                  <FileText className="mr-2 h-3.5 w-3.5" />
                  Open review content
                </Button>
              ) : null}
            </div>
          </section>

          <section aria-label="Relationship tools" className="space-y-3 border-t border-[#d8c7ae]/60 pt-5 dark:border-[#d9c8ae]/15">
            <div className="flex items-baseline justify-between gap-3 px-1">
              <h2 className="text-sm font-semibold text-foreground">Relationship actions</h2>
              <p className="text-xs text-muted-foreground">Act on this relationship now</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" className="min-h-10 rounded-full border-[#d8c7ae]/80 bg-[#fbf8f3] px-3 text-[#4c382c] hover:bg-[#f4eadc] dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f3e6d5] dark:hover:bg-white/[0.08]" aria-label={messageLabel} onClick={onMessage}>
                <MessageSquare className="mr-2 h-3.5 w-3.5" />
                Message
                {unreadMessageCount && unreadMessageCount > 0 ? (
                  <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">{unreadMessageCount}</span>
                ) : null}
              </Button>
              <Button type="button" variant="outline" className="min-h-10 rounded-full border-[#d8c7ae]/80 bg-[#fbf8f3] px-3 text-[#4c382c] hover:bg-[#f4eadc] dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f3e6d5] dark:hover:bg-white/[0.08]" disabled={privateNoteStatus === "unavailable"} onClick={onAddPrivateNote}>
                <NotebookPen className="mr-2 h-3.5 w-3.5" />
                {privateNoteStatus === "unavailable" ? "Private notes unavailable" : "Add private note"}
              </Button>
              <Button type="button" variant="outline" className="min-h-10 rounded-full border-[#d8c7ae]/80 bg-[#fbf8f3] px-3 text-[#4c382c] hover:bg-[#f4eadc] dark:border-[#d9c8ae]/20 dark:bg-white/[0.03] dark:text-[#f3e6d5] dark:hover:bg-white/[0.08]" onClick={onUploadTranscript}>
                <Upload className="mr-2 h-3.5 w-3.5" />
                Upload transcript
              </Button>
            </div>
            {privateNoteOpen && privateNoteEditor ? (
              <div className="rounded-2xl border border-border/60 bg-background p-3">{privateNoteEditor}</div>
            ) : null}
          </section>
        </div>
      </div>
    </section>
  );
}
